/**
 * Which chat forms are open and unsent (14 Oct): each form card reports
 * itself here, and the ask page swaps the composer for a slim bar while one
 * is open. See `lib/inline-forms.ts`.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { withForm } from "./lib/inline-forms";

interface InlineForms {
  open: readonly string[];
  report: (key: string, isOpen: boolean) => void;
}

const InlineFormsContext = createContext<InlineForms | null>(null);

export function InlineFormsProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<string[]>([]);
  const report = useCallback((key: string, isOpen: boolean) => {
    setOpen((previous) => {
      const next = withForm(previous, key, isOpen);
      return next.length === previous.length ? previous : next;
    });
  }, []);
  return <InlineFormsContext value={{ open, report }}>{children}</InlineFormsContext>;
}

/** The open forms' keys (empty outside the ask page). */
export function useOpenInlineForms(): readonly string[] {
  return useContext(InlineFormsContext)?.open ?? [];
}

/** A form card says whether it is open and unsent; unmounting closes it. */
export function useInlineForm(key: string, isOpen: boolean) {
  const report = useContext(InlineFormsContext)?.report;
  useEffect(() => {
    report?.(key, isOpen);
    return () => report?.(key, false);
  }, [report, key, isOpen]);
}
